/**
 * AddCardComposer — inline "+ Add a card" affordance pinned to the
 * bottom of every lane. Trello-style:
 *
 *   collapsed:   [ + Add a card ]                ← ghost button
 *   expanded:    ┌───────────────────────────┐
 *                │ Enter a title for this card│  ← textarea, ⌘+Enter to save
 *                └───────────────────────────┘
 *                [ Add card ] [ × ]
 *
 * Why this lives in @quotech/ui rather than apps/sam: every tenant
 * that consumes <KanbanLane> wants the same "create a manual card"
 * affordance. The backend (`POST /api/kanban/cards/`) is identical
 * across tenants — the only thing that changes is the org id, which
 * the parent already provides via the `lane.board → board.organisation`
 * chain. Without this control, the only way cards get created is
 * via email classification or backfill scripts — there's no way for
 * the operator to add a one-off "follow up with John" card. That's
 * the gap that prompted "so many fucking features missing" on
 * 2026-05-02.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'

import {
  CARD_TITLE_COUNTER_THRESHOLD,
  CARD_TITLE_MAX_LEN,
  splitCardTitle,
} from '../splitCardTitle'

export interface AddCardComposerProps {
  /** Called with the typed title and (optionally) a summary that overflowed
   *  the title cap. Older host bundles that consume only the title will
   *  ignore the second arg — the BE accepts summary as an optional field
   *  on POST /cards/ already. Returning a promise lets us show a spinner +
   *  disable the form until the network round-trip finishes. Throwing
   *  surfaces an inline error message. */
  onCreate: (title: string, summary?: string) => Promise<unknown> | void
  /** Optional placeholder override (e.g. "Add another address line"). */
  placeholder?: string
  /** Optional CTA label override. Defaults to "Add a card" / "Add card". */
  collapsedLabel?: string
  expandedLabel?: string
}

export function AddCardComposer({
  onCreate,
  placeholder = 'Enter a title for this card…',
  collapsedLabel = 'Add a card',
  expandedLabel = 'Add card',
}: AddCardComposerProps) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Live preview of how the current draft will be clipped before submit.
  // Pure-fn output so cheap to recompute every keystroke; the operator
  // sees the character counter colour-shift as they approach the cap,
  // and we can pre-render a "title will be truncated" affordance when
  // they're about to lose tail characters.
  const preview = useMemo(() => splitCardTitle({ raw: title }), [title])
  const charCount = title.length
  // After 2026-05-19 splitCardTitle no longer emits a description.
  // `split` means the title itself was clipped — show the same
  // truncation warning the operator gets after submit.
  const willTruncate = preview.split
  const showCounter = charCount >= CARD_TITLE_COUNTER_THRESHOLD || willTruncate

  // Auto-focus when expanding.
  useEffect(() => {
    if (open && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [open])

  // Click-outside collapses the composer (standard Trello behaviour
  // — feels chatty if it sticks open after you click away).
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        // Don't auto-collapse mid-typing — stash the partial input for
        // the next time the operator opens the composer in this lane.
        if (!title.trim()) setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, title])

  const handleSubmit = async () => {
    const { title: clipTitle, split } = splitCardTitle({ raw: title })
    if (!clipTitle || busy) return
    setBusy(true)
    setError(null)
    setHint(null)
    try {
      // splitCardTitle no longer produces a description (operator
      // complaint 2026-05-19); pass nothing for the second arg so
      // the BE keeps `summary=""`. The signature still accepts a
      // second arg for backward compatibility with host bundles
      // that haven't redeployed.
      await onCreate(clipTitle, undefined)
      setTitle('')
      if (split) {
        // Soft confirmation so the operator knows their tail-end
        // characters didn't land. Different wording from the
        // previous "moved overflow into description" — now we
        // explicitly say the title was truncated.
        setHint(`Title truncated to ${CARD_TITLE_MAX_LEN} characters.`)
      }
      // Keep composer open so the operator can quickly add several
      // cards in a row — Trello does the same. Closing on the first
      // save would force a click-to-reopen for every new card.
      textareaRef.current?.focus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Stage 3 composer polish: a transparent border that warms to a
        // dashed hint on hover signals "this opens an input" without adding
        // visual weight to every quiet lane footer.
        className="group/add-card flex w-full items-center gap-1.5 rounded-md border border-dashed border-transparent px-2 py-1 text-left text-[11.5px] font-medium text-neutral-500 dark:text-white/85 hover:border-neutral-300/70 hover:bg-neutral-200/50 hover:text-neutral-800 dark:hover:border-white/20 dark:hover:bg-white/10 dark:hover:text-white transition-colors"
      >
        <Plus className="h-3 w-3" />
        {collapsedLabel}
      </button>
    )
  }

  return (
    <div ref={wrapRef} className="space-y-2">
      <textarea
        ref={textareaRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          // Enter (no shift) submits — most users expect this. Shift+
          // Enter inserts a newline (rare for card titles, but cheap
          // to support).
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void handleSubmit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            setOpen(false)
            setTitle('')
            setError(null)
          }
        }}
        placeholder={placeholder}
        rows={2}
        disabled={busy}
        className="w-full resize-none rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2 py-1.5 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-60"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!title.trim() || busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {expandedLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            setTitle('')
            setError(null)
          }}
          aria-label="Cancel"
          className="rounded p-1.5 text-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-800 hover:text-neutral-800 dark:hover:text-neutral-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        {error && (
          <span className="text-[11px] text-red-600 truncate">{error}</span>
        )}
        {!error && hint && (
          <span className="text-[11px] text-emerald-600 truncate">{hint}</span>
        )}
        {showCounter && (
          <span
            className={[
              'ml-auto text-[10.5px] tabular-nums',
              willTruncate
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-neutral-500 dark:text-neutral-400',
            ].join(' ')}
            title={
              willTruncate
                ? `Title will be truncated to ${CARD_TITLE_MAX_LEN} characters.`
                : `Title length ${charCount} / ${CARD_TITLE_MAX_LEN}`
            }
          >
            {charCount}/{CARD_TITLE_MAX_LEN}
            {willTruncate ? ' · truncated' : ''}
          </span>
        )}
      </div>
    </div>
  )
}
